import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MapPin,
  User,
  ArrowLeft,
  Loader2,
  AlertCircle,
  Navigation,
  MapPinned,
} from "lucide-react";
import { routesAPI } from "@/services/routes";
import Card from "@/components/common/Card";
import Badge from "@/components/common/Badge";
import Button from "@/components/common/Button";
import RouteDetailMap from "@/components/map/RouteDetailMap";
import Tabs from "@/components/common/Tabs";
import StateInsights from "@/utils/StateInsights";
import { INDIAN_STATES } from "@/utils/constants";
import { getImageUrl } from "@/utils/imageUrl";

const RouteDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("heritage");

  useEffect(() => {
    const fetchRouteDetails = async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await routesAPI.getRouteById(id);
        setRoute(data);
      } catch (err) {
        console.error("Error fetching route details:", err);
        setError(err.response?.data?.message || "Failed to load route details");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchRouteDetails();
    }
  }, [id]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-green-600 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">
            Loading route details...
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !route) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
            Route Not Found
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {error || "The route you are looking for does not exist."}
          </p>
          <Button onClick={() => navigate("/")} variant="primary">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  // Get difficulty badge tone
  const getDifficultyTone = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case "easy":
        return "success";
      case "medium":
        return "info";
      case "hard":
        return "danger";
      default:
        return "muted";
    }
  };

  // Get state name from state code
  const getStateName = (stateCode) => {
    const state = INDIAN_STATES.find((s) => s.code === stateCode);
    return state ? state.name : stateCode;
  };

  // Helper function to render insight content
  const renderInsightContent = (items) => {
    if (!items || items.length === 0) {
      return (
        <p className="text-gray-500 dark:text-gray-400 text-center py-8">
          No information available yet. Check back soon!
        </p>
      );
    }

    return (
      <div className="space-y-6">
        {items.map((item, index) => {
          if (item.type === "text") {
            return (
              <p
                key={index}
                className="text-base text-gray-700 dark:text-gray-300 leading-relaxed"
              >
                {item.content}
              </p>
            );
          } else if (item.type === "image") {
            return (
              <div key={index} className="rounded-lg overflow-hidden">
                <img
                  src={item.src}
                  alt={item.alt}
                  className="w-full h-auto object-cover"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              </div>
            );
          }
          return null;
        })}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      {/* Header with back button */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="container mx-auto px-4 py-4">
          <Button
            onClick={() => navigate(-1)}
            variant="secondary"
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Route Title Section */}
          <div className="mb-8">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-4xl font-bold text-gray-900 dark:text-white">
                {route.name}
              </h1>
              <Badge tone={getDifficultyTone(route.difficulty)} size="md">
                {route.difficulty}
              </Badge>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Map Section - Takes 2 columns on large screens */}
            <div className="lg:col-span-2">
              <Card className="!p-0 overflow-hidden">
                <div className="h-96 lg:h-[600px] w-full">
                  <RouteDetailMap route={route} />
                </div>
              </Card>
            </div>

            {/* Details Section - 1 column on large screens */}
            <div className="space-y-6">
              {/* Route Stats */}
              <Card title="Route Information">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Navigation className="w-5 h-5 text-green-600 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Distance
                      </p>
                      <p className="text-lg font-semibold text-gray-900 dark:text-white">
                        {route.distance ? `${route.distance} km` : "N/A"}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Start Point
                      </p>
                      <p className="text-base font-medium text-gray-900 dark:text-white">
                        {route.startPoint || "Not specified"}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-red-600 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        End Point
                      </p>
                      <p className="text-base font-medium text-gray-900 dark:text-white">
                        {route.endPoint || "Not specified"}
                      </p>
                    </div>
                  </div>

                  {route.stateCode && (
                    <div className="flex items-start gap-3">
                      <MapPinned className="w-5 h-5 text-purple-600 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          State
                        </p>
                        <p className="text-base font-medium text-gray-900 dark:text-white">
                          {route.stateCode}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </Card>

              {/* Creator Information */}
              {route.creator && (
                <Card title="Route Creator">
                  <div className="flex items-center gap-4">
                    {route.creator.avatar ? (
                      <img
                        src={getImageUrl(route.creator.avatar)}
                        alt={route.creator.name}
                        className="w-16 h-16 rounded-full object-cover border-2 border-green-500"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center border-2 border-green-500">
                        <User className="w-8 h-8 text-green-600" />
                      </div>
                    )}
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white text-lg">
                        {route.creator.name}
                      </p>
                      {route.creator.username && (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          @{route.creator.username}
                        </p>
                      )}
                    </div>
                  </div>
                </Card>
              )}

              {/* Route Description */}
              {route.description && (
                <Card title="Route Description">
                  <p className="text-base text-gray-700 dark:text-gray-300 leading-relaxed">
                    {route.description}
                  </p>
                </Card>
              )}
            </div>
          </div>

          {/* State Insights Section */}
          {route.stateCode && StateInsights[route.stateCode] && (
            <div className="mt-12">
              <Card>
                <div className="space-y-6">
                  {/* Section Header */}
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                      Explore {getStateName(route.stateCode)}
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400">
                      Discover the rich heritage, culture, tourism, and cuisine
                      of this region
                    </p>
                  </div>

                  {/* Tabs Navigation */}
                  <Tabs
                    tabs={[
                      { id: "heritage", label: "Heritage" },
                      { id: "culture", label: "Culture" },
                      { id: "tourism", label: "Tourism" },
                      { id: "cuisine", label: "Cuisine" },
                    ]}
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                  />

                  {/* Tab Content */}
                  <div className="pt-6">
                    {renderInsightContent(
                      StateInsights[route.stateCode][activeTab]
                    )}
                  </div>
                </div>
              </Card>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default RouteDetails;
